import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiDateInputComponent } from '../../atoms/date-input/date-input.component';
import { UiInputComponent } from '../../atoms/input/input.component';
import { UiMultiselectComponent } from '../../atoms/multiselect/multiselect.component';
import { UiSelectComponent, UiSelectOption } from '../../atoms/select/select.component';
import { UiTagComponent } from '../../atoms/tag/tag.component';
import { UiTextareaComponent } from '../../atoms/textarea/textarea.component';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { UiDrawerComponent } from '../../molecules/drawer/drawer.component';
import { UiFileUploadComponent } from '../../molecules/file-upload/file-upload.component';
import { UiFileItem } from '../../molecules/file-upload/file-upload.types';
import { UiFormSectionComponent } from '../../molecules/form-section/form-section.component';
import {
  UiDataTableActionMenuState,
  UiDataTableActionsMenuComponent,
} from '../data-table/data-table-actions-menu.component';
import { UiDataTablePaginationComponent } from '../data-table/data-table-pagination.component';
import {
  getPagedRows,
  getRangeEnd,
  getRangeStart,
  getTotalPages,
  getVisiblePages,
} from '../data-table/data-table.pagination';
import { DATA_TABLE_MULTI_FILTER_SEP, parseColumnMultiFilter, RowAction } from '../data-table/data-table.types';
import {
  UiDocumentFolderFile,
  UiDocumentFolderFileAction,
  UiDocumentFolderFileActionEvent,
  UiDocumentFolderFileKind,
  UiDocumentFolderItem,
  UiDocumentFolderStatus,
} from './document-folder-list.types';

type StatusFilter = 'all' | 'current' | 'none' | 'pending' | 'expired';

type ColumnFilterKey = 'document' | 'type' | 'validity' | 'updated' | 'docs' | 'status';

type ColumnFilters = Record<ColumnFilterKey, string>;

const PAGE_SIZES = [10, 25, 50, 100];

const PAGE_SIZE_OPTIONS: UiSelectOption[] = PAGE_SIZES.map(size => ({
  id: String(size),
  label: `${size} / página`,
  value: size,
}));

const FILE_ROW_ACTIONS: readonly RowAction[] = [
  { key: 'view', label: 'Ver' },
  { key: 'edit', label: 'Editar' },
  { key: 'delete', label: 'Eliminar', destructive: true },
];

const FILE_ICON: Record<UiDocumentFolderFileKind, string> = {
  pdf: 'icons/ic_doc_pdf.svg',
  excel: 'icons/ic_doc_xls.svg',
  powerpoint: 'icons/ic_doc_ppt.svg',
  other: 'icons/ic_doc_file.svg',
};

const TYPE_FILTER_OPTIONS: UiSelectOption[] = [
  { id: 'obligatorio', label: 'Obligatorio', value: 'Obligatorio' },
  { id: 'opcional', label: 'Opcional', value: 'Opcional' },
];

const STATUS_FILTER_OPTIONS: UiSelectOption[] = [
  { id: 'vigente', label: 'Vigente', value: 'Vigente' },
  { id: 'pendiente', label: 'Pendiente', value: 'Pendiente' },
  { id: 'vencido', label: 'Vencido', value: 'Vencido' },
];

type MultiFilterKey = 'document' | 'type' | 'docs' | 'status';

/**
 * Organismo: listado visual de documentos representados como carpetas.
 * Es presentacional, filtra localmente y emite acciones; no conoce dominio ni API.
 */
@Component({
  selector: 'ui-document-folder-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiButtonComponent,
    UiDataTableActionsMenuComponent,
    UiDateInputComponent,
    UiDrawerComponent,
    UiFileUploadComponent,
    UiDataTablePaginationComponent,
    UiFormSectionComponent,
    UiInputComponent,
    UiMultiselectComponent,
    UiSelectComponent,
    UiTagComponent,
    UiTextareaComponent,
    UiTooltipDirective,
  ],
  templateUrl: './document-folder-list.component.html',
  styleUrl: './document-folder-list.component.scss',
  host: { class: 'ui-document-folder-list-host' },
})
export class UiDocumentFolderListComponent {
  readonly items = input<readonly UiDocumentFolderItem[]>([]);

  readonly exportExcel = output<void>();
  readonly fileAction = output<UiDocumentFolderFileActionEvent>();
  readonly newClick = output<void>();

  protected readonly statusFilter = signal<StatusFilter>('all');
  protected readonly search = signal('');
  protected readonly columnFilters = signal<ColumnFilters>({
    document: '',
    type: '',
    validity: '',
    updated: '',
    docs: '',
    status: '',
  });
  protected readonly currentPage = signal(0);
  protected readonly currentSize = signal(10);
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  protected readonly selectedSizeOption = computed(
    () =>
      this.pageSizeOptions.find(option => Number(option.value) === this.currentSize()) ??
      this.pageSizeOptions[0],
  );
  protected readonly selectedFolder = signal<UiDocumentFolderItem | null>(null);
  protected readonly fileMenu = signal<UiDataTableActionMenuState | null>(null);
  protected readonly newDocumentOpen = signal(false);
  protected readonly newDocumentValidity = signal('');
  protected readonly newDocumentFiles = signal<UiFileItem[]>([]);
  protected readonly newDocumentNotes = signal('');

  protected readonly statusNav = computed(() => {
    const items = this.items();
    let current = 0;
    let expired = 0;
    let none = 0;
    let pending = 0;
    for (const item of items) {
      if (item.validity === 'Sin vigencia') none += 1;
      else if (item.status === 'Vigente') current += 1;
      else if (item.status === 'Vencido') expired += 1;
      if (this.isPending(item)) pending += 1;
    }

    return [
      { key: 'all' as const, label: 'Todos', count: items.length },
      { key: 'current' as const, label: 'Vigentes', count: current },
      { key: 'none' as const, label: 'Sin vigencia', count: none },
      { key: 'pending' as const, label: 'Pendientes', count: pending },
      { key: 'expired' as const, label: 'Vencidos', count: expired },
    ];
  });

  protected readonly visibleItems = computed(() => {
    const query = this.search().trim().toLocaleLowerCase('es');
    const filter = this.statusFilter();

    return this.items().filter(item => {
      const matchesStatus =
        filter === 'all' ||
        (filter === 'current' && item.status === 'Vigente') ||
        (filter === 'none' && item.validity === 'Sin vigencia') ||
        (filter === 'pending' && this.isPending(item)) ||
        (filter === 'expired' && item.status === 'Vencido');

      if (!matchesStatus) return false;
      if (!this.matchesColumnFilters(item)) return false;
      if (!query) return true;

      return [
        item.title,
        item.type,
        this.validityLabel(item),
        this.statusLabel(item),
        item.lastUpdated,
        String(item.attachments),
      ]
        .join(' ')
        .toLocaleLowerCase('es')
        .includes(query);
    });
  });

  protected readonly selectedFiles = computed(
    () => this.selectedFolder()?.files ?? [],
  );

  protected readonly total = computed(() => this.visibleItems().length);
  protected readonly totalPages = computed(() =>
    getTotalPages(this.total(), this.currentSize()),
  );
  protected readonly visiblePages = computed(() =>
    getVisiblePages(this.totalPages(), this.safePage()),
  );
  protected readonly rangeStart = computed(() =>
    getRangeStart(this.total(), this.safePage(), this.currentSize()),
  );
  protected readonly rangeEnd = computed(() =>
    getRangeEnd(this.total(), this.safePage(), this.currentSize()),
  );
  protected readonly pagedItems = computed(() =>
    getPagedRows(this.visibleItems(), this.safePage(), this.currentSize()),
  );

  protected isPending(item: UiDocumentFolderItem): boolean {
    return item.attachments === 0 || !item.status;
  }

  protected statusLabel(item: UiDocumentFolderItem): string {
    return this.isPending(item) ? 'Pendiente' : (item.status ?? '');
  }

  protected readonly typeFilterOptions = TYPE_FILTER_OPTIONS;
  protected readonly statusFilterOptions = STATUS_FILTER_OPTIONS;
  protected readonly documentFilterOptions = computed<UiSelectOption[]>(() => {
    const titles = [...new Set(this.items().map(item => item.title))];
    return titles.map(title => ({ id: title, label: title, value: title }));
  });
  protected readonly docsFilterOptions = computed<UiSelectOption[]>(() => {
    const counts = [...new Set(this.items().map(item => item.attachments))].sort((a, b) => a - b);
    return counts.map(count => ({
      id: String(count),
      label: String(count),
      value: String(count),
    }));
  });

  protected onColumnFilter(key: ColumnFilterKey, value: string): void {
    this.columnFilters.update(current => ({ ...current, [key]: value }));
    this.currentPage.set(0);
  }

  protected onColumnFilterMulti(key: MultiFilterKey, options: UiSelectOption[]): void {
    const value = options
      .map(option => String(option.value ?? option.label))
      .join(DATA_TABLE_MULTI_FILTER_SEP);
    this.onColumnFilter(key, value);
  }

  protected columnFilterSelected(key: MultiFilterKey): UiSelectOption[] {
    const options = this.columnFilterOptions(key);
    const selected = new Set(parseColumnMultiFilter(this.columnFilters()[key]));
    if (!selected.size) return [];
    return options.filter(option => selected.has(String(option.value ?? option.label)));
  }

  private columnFilterOptions(key: MultiFilterKey): UiSelectOption[] {
    switch (key) {
      case 'document':
        return this.documentFilterOptions();
      case 'type':
        return TYPE_FILTER_OPTIONS;
      case 'status':
        return STATUS_FILTER_OPTIONS;
      case 'docs':
        return this.docsFilterOptions();
    }
  }

  private matchesColumnFilters(item: UiDocumentFolderItem): boolean {
    const filters = this.columnFilters();
    const values: Record<ColumnFilterKey, string> = {
      document: item.title,
      type: item.type,
      validity: this.validityLabel(item),
      updated: item.lastUpdated,
      docs: String(item.attachments),
      status: this.statusLabel(item),
    };

    return (Object.keys(values) as ColumnFilterKey[]).every(key => {
      const query = filters[key].trim();
      if (!query) return true;
      if (key === 'document' || key === 'type' || key === 'docs' || key === 'status') {
        const selected = parseColumnMultiFilter(query);
        return !selected.length || selected.includes(values[key]);
      }
      return values[key].toLocaleLowerCase('es').includes(query.toLocaleLowerCase('es'));
    });
  }

  protected validityLabel(item: UiDocumentFolderItem): string {
    if (item.validity !== 'Con vigencia') return item.validity;

    const term = item.validityTerm ?? '30 días';
    const until = item.validityUntil ?? '14/09/2026';
    return `${item.validity} - ${term} - ${until}`;
  }

  protected selectStatus(filter: StatusFilter): void {
    this.statusFilter.set(filter);
    this.currentPage.set(0);
    if (this.selectedFolder()) this.closeFolder();
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    this.currentPage.set(0);
  }

  protected onPageSizeChange(option: UiSelectOption | null): void {
    const next = Number(option?.value ?? option?.id ?? 10);
    this.currentSize.set(PAGE_SIZES.includes(next) ? next : 10);
    this.currentPage.set(0);
  }

  protected goToPage(page: number): void {
    this.currentPage.set(page);
  }

  protected safePage(): number {
    return Math.min(this.currentPage(), Math.max(0, this.totalPages() - 1));
  }

  protected openNewDocument(): void {
    this.newDocumentValidity.set('');
    this.newDocumentFiles.set([]);
    this.newDocumentNotes.set('');
    this.newDocumentOpen.set(true);
    this.newClick.emit();
  }

  constructor() {
    const onScroll = () => this.closeFileMenu();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('scroll', onScroll, { capture: true }),
    );
  }

  protected openFolder(item: UiDocumentFolderItem): void {
    this.closeFileMenu();
    this.selectedFolder.set(item);
  }

  protected closeFolder(): void {
    this.closeFileMenu();
    this.selectedFolder.set(null);
  }

  protected fileIcon(kind: UiDocumentFolderFileKind): string {
    return FILE_ICON[kind];
  }

  protected toggleFileMenu(
    event: MouseEvent,
    file: UiDocumentFolderFile,
    idx: number,
  ): void {
    event.stopPropagation();
    if (this.fileMenu()?.rowIndex === idx) {
      this.closeFileMenu();
      return;
    }

    const td = (event.currentTarget as HTMLElement).closest('td') as HTMLElement;
    const rect = (td ?? (event.currentTarget as HTMLElement)).getBoundingClientRect();
    const estimatedMenuH = FILE_ROW_ACTIONS.length * 36 + 8;
    const openAbove = rect.top + estimatedMenuH > window.innerHeight;

    this.fileMenu.set({
      row: file,
      rowIndex: idx,
      top: openAbove ? rect.bottom : rect.top,
      left: rect.left,
      openAbove,
      items: FILE_ROW_ACTIONS,
    });
  }

  protected onFileMenuAction(action: RowAction): void {
    const menu = this.fileMenu();
    const folder = this.selectedFolder();
    this.closeFileMenu();
    if (!menu || !folder) return;

    this.fileAction.emit({
      folder,
      file: menu.row as UiDocumentFolderFile,
      action: action.key as UiDocumentFolderFileAction,
    });
  }

  protected closeFileMenu(): void {
    this.fileMenu.set(null);
  }

  protected statusColor(item: UiDocumentFolderItem): 'success' | 'danger' {
    return item.status === 'Vencido' ? 'danger' : 'success';
  }

  protected fileStatusColor(status: UiDocumentFolderStatus): 'success' | 'danger' {
    return status === 'Vencido' ? 'danger' : 'success';
  }

  @HostListener('document:click')
  protected closeFileMenuOnDocumentClick(): void {
    this.closeFileMenu();
  }

  @HostListener('document:keydown.escape')
  protected closeFileMenuOnEscape(): void {
    this.closeFileMenu();
  }

  @HostListener('window:resize')
  protected closeFileMenuOnResize(): void {
    this.closeFileMenu();
  }
}
